import React, { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../services/axiosInstance'; // Importera axiosInstance
import {
  Box,
  Button,
  Modal,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemIcon,
  FormControlLabel,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  Chip,
  Menu
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FullPageLayoutProfile, ITemplate, ICategory } from '../types/damTypes';
import { API_BASE_URL } from '../config';
import TemplateFieldEditor from './TemplateFieldEditor';
import {
  FULL_PAGE_LAYOUT_PROFILE_OPTIONS,
  normalizeFullPageLayoutProfile,
} from '../modules/fullPageAssetDetail/layoutProfiles';

interface TemplateManagementModalProps {
  open: boolean;
  onClose: () => void;
  categories: ICategory[];
  onTemplateAdded?: () => void;
  onTemplateUpdated?: () => void;
}

// Define available external systems
const EXTERNAL_SYSTEMS = [
  {
    id: 'validoo',
    name: 'Validoo',
    description: 'Validoo API för produktinformation via GS1 Trade Item API. Använder token-baserad autentisering med användarnamn/lösenord tillsammans med client ID/secret.',
    requiresAuth: true,
    fields: [
      // Baserat på GS1/Validoo Trade Item API
      { id: 'gtin', name: 'GTIN', description: 'Global Trade Item Number' },
      { id: 'tradeItemDescription', name: 'Produktnamn', description: 'Produktens namn' },
      { id: 'brandName', name: 'Varumärke', description: 'Produktens varumärke' },
      { id: 'functionalName', name: 'Funktionellt namn', description: 'Produktens funktionella namn' },
      { id: 'descriptiveSize', name: 'Storlek (beskrivning)', description: 'Beskrivande storlek, t.ex. "750 ml"' },
      { id: 'description', name: 'Beskrivning', description: 'Detaljerad produktbeskrivning' },
      { id: 'height', name: 'Höjd', description: 'Produktens höjd i mm' },
      { id: 'width', name: 'Bredd', description: 'Produktens bredd i mm' },
      { id: 'depth', name: 'Djup', description: 'Produktens djup i mm' },
      { id: 'grossWeight', name: 'Bruttovikt', description: 'Produktens bruttovikt i gram' },
      { id: 'drainedWeight', name: 'Avrunnen vikt', description: 'Produktens avrunna vikt i gram' },
      { id: 'netContent', name: 'Nettoinnehåll', description: 'Produktens nettoinnehåll' },
      { id: 'ingredientStatement', name: 'Ingredienser', description: 'Lista över ingredienser' },
      { id: 'nutritionalClaim', name: 'Näringsvärde', description: 'Produktens näringsvärden' },
      { id: 'allergenStatement', name: 'Allergener', description: 'Information om allergener' },
      { id: 'countryOfOrigin', name: 'Ursprungsland', description: 'Produktens ursprungsland' },
      { id: 'manufacturerName', name: 'Tillverkare', description: 'Tillverkarens namn' },
      { id: 'storageInstructions', name: 'Förvaringsanvisningar', description: 'Hur produkten ska förvaras' },
      { id: 'packagingType', name: 'Förpackningstyp', description: 'Typ av förpackning' },
    ]
  },
  {
    id: 'dabas',
    name: 'Dabas',
    description: 'Dabas API för produktinformation. Använder en global API-nyckel konfigurerad på servern.',
    requiresAuth: false,
    fields: [
      // Dabas-fält - dessa hämtas dynamiskt från API:et
      { id: 'Produktnamn', name: 'Produktnamn', description: 'Produktens namn' },
      { id: 'Artikelbenamning', name: 'Artikelbenämning', description: 'Artikelbenämning' },
      { id: 'Ingrediensforteckning', name: 'Ingrediensförteckning', description: 'Lista över ingredienser' },
      { id: 'Forvaringsinstruktion', name: 'Förvaringsinstruktion', description: 'Hur produkten ska förvaras' },
    ]
  },
  {
    id: 'erp',
    name: 'ERP System',
    description: 'Integration med företagets ERP-system för att hämta produktinformation',
    requiresAuth: true,
    fields: [
      // ERP-fält som exempel
      { id: 'articleNumber', name: 'Artikelnummer', description: 'Internt artikelnummer' },
      { id: 'productName', name: 'Produktnamn', description: 'Produktens namn i ERP-systemet' },
      { id: 'purchasePrice', name: 'Inköpspris', description: 'Produktens inköpspris' },
      { id: 'salesPrice', name: 'Försäljningspris', description: 'Produktens försäljningspris' },
      { id: 'supplier', name: 'Leverantör', description: 'Produktens leverantör' },
      { id: 'stock', name: 'Lagersaldo', description: 'Aktuellt lagersaldo' },
      { id: 'minimumStock', name: 'Minimum lagersaldo', description: 'Lägsta tillåtna lagersaldo' },
      { id: 'reorderPoint', name: 'Beställningspunkt', description: 'Lagernivå för återbeställning' },
    ]
  },
  {
    id: 'custom',
    name: 'Custom API',
    description: 'Integration med anpassat API för att hämta data',
    requiresAuth: false,
    fields: [
      // Generiska fält som exempel
      { id: 'id', name: 'ID', description: 'Unikt ID för objektet' },
      { id: 'name', name: 'Namn', description: 'Objektets namn' },
      { id: 'description', name: 'Beskrivning', description: 'Beskrivning av objektet' },
      { id: 'customField1', name: 'Anpassat fält 1', description: 'Anpassat fält 1' },
      { id: 'customField2', name: 'Anpassat fält 2', description: 'Anpassat fält 2' },
      { id: 'customField3', name: 'Anpassat fält 3', description: 'Anpassat fält 3' },
    ]
  }
];

// Temporary definition for IExternalIntegration to resolve linter errors
// We will address this properly later.
type IExternalIntegration = any;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const TemplateManagementModal: React.FC<TemplateManagementModalProps> = ({
  open,
  onClose,
  categories,
  onTemplateAdded,
  onTemplateUpdated
}) => {
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  
  // State for template action menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  
  // State for template editing
  const [editingTemplate, setEditingTemplate] = useState<ITemplate | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [templateDescription, setTemplateDescription] = useState<string>('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [templateFields, setTemplateFields] = useState<any[]>([]);
  const [hideEmptyFieldsInDisplay, setHideEmptyFieldsInDisplay] = useState<boolean>(false);
  const [fullPageLayoutProfile, setFullPageLayoutProfile] = useState<FullPageLayoutProfile>('other');
  
  // State for external integrations
  const [selectedExternalSystem, setSelectedExternalSystem] = useState<string>('');
  const [externalApiEndpoint, setExternalApiEndpoint] = useState<string>('');
  const [externalApiKey, setExternalApiKey] = useState<string>('');
  const [externalClientId, setExternalClientId] = useState<string>('');
  const [externalClientSecret, setExternalClientSecret] = useState<string>('');
  const [externalRefreshInterval, setExternalRefreshInterval] = useState<number>(1440); // Default to daily
  const [editingIntegrationIndex, setEditingIntegrationIndex] = useState<number>(-1);
  const [isEditingIntegration, setIsEditingIntegration] = useState<boolean>(false);
  const [externalIntegrations, setExternalIntegrations] = useState<Array<{
    system: string;
    displayName?: string;
    fieldMapping: Record<string, string>;
    apiEndpoint?: string;
    apiKey?: string;
    clientId?: string;
    clientSecret?: string;
    refreshInterval?: number;
    lastSynced?: Date;
    enabled: boolean;
    username?: string;
    password?: string;
  }>>([]);
  
  // Lägg till dessa två state-variabler nära de andra externalXXX variablerna
  const [externalUsername, setExternalUsername] = useState('');
  const [externalPassword, setExternalPassword] = useState('');
  const [externalDisplayName, setExternalDisplayName] = useState('');

  const categoryParentById = React.useMemo(() => {
    const map = new Map<string, string | null>();
    categories.forEach((category) => {
      const parent = category.parentCategory;
      const parentId = typeof parent === 'string'
        ? parent
        : (parent && typeof parent === 'object' ? (parent as ICategory)._id : null);
      map.set(category._id, parentId || null);
    });
    return map;
  }, [categories]);

  const wineRootId = React.useMemo(() => {
    const exactRoot = categories.find(
      (category) => (category.name || '').trim().toLowerCase() === 'vin' && !category.parentCategory,
    );
    const fallback = categories.find(
      (category) => (category.name || '').trim().toLowerCase() === 'vin',
    );
    return exactRoot?._id || fallback?._id || null;
  }, [categories]);

  const isWineTemplate = React.useMemo(() => {
    if (!wineRootId) return false;

    const isDescendant = (id: string): boolean => {
      let current: string | null | undefined = id;
      while (current) {
        if (current === wineRootId) return true;
        current = categoryParentById.get(current) || null;
      }
      return false;
    };

    return selectedCategoryIds.some((id) => id === wineRootId || isDescendant(id));
  }, [selectedCategoryIds, wineRootId, categoryParentById]);
  
  // State for testing Dabas connection
  const [dabasTestGtin, setDabasTestGtin] = useState('07316150970340'); // Default test GTIN
  const [dabasTestResult, setDabasTestResult] = useState<any>(null);
  const [dabasTestLoading, setDabasTestLoading] = useState(false);
  
  // Fetch templates on component mount
  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);
  
  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/api/templates`);
      setTemplates(response.data);
    } catch (err: any) {
      setError(`Failed to load templates: ${err.message}`);
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, templateId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTemplateId(templateId);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTemplateId(null);
  };
  
  const handleAddNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setSelectedCategoryIds([]);
    setTemplateFields([]);
    setHideEmptyFieldsInDisplay(false);
    setFullPageLayoutProfile('other');
    setTabValue(1); // Switch to "Edit Template" tab
  };
  
  const handleEditTemplate = (template: ITemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setSelectedCategoryIds(template.categoryIds);
    setTemplateFields(template.fields || []);
    setExternalIntegrations(template.externalIntegrations || []);
    setHideEmptyFieldsInDisplay(template.hideEmptyFieldsInDisplay === true);
    setFullPageLayoutProfile(normalizeFullPageLayoutProfile(template.fullPageLayoutProfile));
    setTabValue(1); // Switch to "Edit Template" tab
  };
  
  const handleDeleteTemplate = async (templateId: string) => {
    handleMenuClose(); // Close menu first
    
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/api/templates/${templateId}`);
      setTemplates(prevTemplates => prevTemplates.filter(template => template._id !== templateId));
    } catch (err: any) {
      setError(`Failed to delete template: ${err.message}`);
      console.error('Error deleting template:', err);
    }
  };
  
  const handleSetDefaultTemplate = async (templateId: string) => {
    try {
      const response = await axiosInstance.put(`/api/templates/${templateId}/set-default`);
      setTemplates(prevTemplates => prevTemplates.map(template => ({
        ...template,
        isDefault: template._id === templateId
      })));
    } catch (err: any) {
      setError(`Failed to set default template: ${err.message}`);
      console.error('Error setting default template:', err);
    }
  };
  
  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const categoryId = event.target.value;
    setSelectedCategoryIds(prevSelectedIds => {
      if (prevSelectedIds.includes(categoryId)) {
        return prevSelectedIds.filter(id => id !== categoryId);
      } else {
        return [...prevSelectedIds, categoryId];
      }
    });
  };
  
  const handleFieldsChange = (updatedFields: any[]) => {
    console.log('Fields updated in TemplateManagementModal:', updatedFields.length, updatedFields);
    
    // Create a deep copy to ensure changes are tracked properly
    // This is crucial for React to detect changes in objects within arrays
    const deepCopiedFields = JSON.parse(JSON.stringify(updatedFields));
    console.log('Deep copied fields being set to state:', deepCopiedFields.length, deepCopiedFields);
    
    setTemplateFields(deepCopiedFields);
  };
  
  const handleAddExternalIntegration = () => {
    setSelectedExternalSystem('');
    setExternalApiEndpoint('');
    setExternalApiKey(''); // Reset API key
    setExternalClientId('');
    setExternalClientSecret('');
    setExternalUsername('');
    setExternalPassword('');
    setExternalDisplayName('');
    setExternalRefreshInterval(1440);
    setIsEditingIntegration(true);
    setEditingIntegrationIndex(-1);
    setDabasTestResult(null);
  };
  
  const handleEditExternalIntegration = (index: number) => {
    const integration = externalIntegrations[index];
    setSelectedExternalSystem(integration.system);
    setExternalApiKey(integration.apiKey || '');
    setExternalClientId(integration.clientId || '');
    setExternalClientSecret(integration.clientSecret || '');
    setExternalUsername(integration.username || '');
    setExternalPassword(integration.password || '');
    setExternalDisplayName((integration as any).displayName || '');
    setExternalApiEndpoint(integration.apiEndpoint || '');
    setExternalRefreshInterval(integration.refreshInterval || 1440);
    setEditingIntegrationIndex(index);
    setIsEditingIntegration(true);
    setDabasTestResult(null);
  };
  
  const handleDeleteExternalIntegration = (index: number) => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      setExternalIntegrations(prevIntegrations => 
        prevIntegrations.filter((_, i) => i !== index)
      );
    }
  };
  
  const handleSaveExternalIntegration = () => {
    // Validera att alla nödvändiga fält finns för Validoo
    if (selectedExternalSystem === 'validoo') {
      if (!externalClientId.trim()) {
        alert('Client ID is required for Validoo integration');
        return;
      }
      if (!externalClientSecret.trim()) {
        alert('Client Secret is required for Validoo integration');
        return;
      }
      if (!externalUsername.trim()) {
        alert('Username is required for Validoo integration');
        return;
      }
      if (!externalPassword.trim()) {
        alert('Password is required for Validoo integration');
        return;
      }
    }
    
    // Skapa/uppdatera integration-objekt med fullständig data
    const integration = {
      system: selectedExternalSystem,
      displayName: externalDisplayName.trim() || undefined,
      apiKey: externalApiKey.trim() || undefined,
      clientId: externalClientId.trim() || undefined,
      clientSecret: externalClientSecret.trim() || undefined,
      username: externalUsername.trim() || undefined,
      password: externalPassword.trim() || undefined,
      apiEndpoint: externalApiEndpoint.trim() || undefined,
      refreshInterval: externalRefreshInterval || 1440, // default: daily
      fieldMapping: {} as Record<string, string>,
      enabled: true,
      lastSynced: undefined
    };
    
    // uppdatera state
    try {
      console.log("Saving integration:", JSON.stringify(integration, null, 2));
      
      // Deklarera med explicit typning
      let updatedIntegrations: Array<{
        system: string;
        fieldMapping: Record<string, string>;
        apiEndpoint?: string;
        apiKey?: string;
        clientId?: string;
        clientSecret?: string;
        username?: string;
        password?: string;
        refreshInterval?: number;
        lastSynced?: Date;
        enabled: boolean;
      }> = [];
      
      if (editingIntegrationIndex >= 0) {
        // Uppdatera befintlig integration
        updatedIntegrations = [...externalIntegrations];
        // Bevara fieldMapping från befintlig integration
        integration.fieldMapping = updatedIntegrations[editingIntegrationIndex].fieldMapping || {};
        updatedIntegrations[editingIntegrationIndex] = integration;
        console.log("Updated existing integration at index:", editingIntegrationIndex);
      } else {
        // Skapa ny integration
        updatedIntegrations = [...externalIntegrations, integration];
        console.log("Added new integration");
      }
      
      console.log("New integrations array:", JSON.stringify(updatedIntegrations, null, 2));
      
      // Spara integrationer till state
      setExternalIntegrations(updatedIntegrations);
      
      // Om vi redigerar en befintlig mall, spara integrationerna omedelbart till servern
      if (editingTemplate) {
        // Omedelbar sparning av integrationer till backend för att förhindra dataförlust
        const saveIntegrationsImmediately = async () => {
          try {
            console.log("Sparar integrationer direkt till backend...");
            
            // Skapa en djupkopia för att undvika referensproblem
            const integrationsCopy = JSON.parse(JSON.stringify(updatedIntegrations));
            
            // Explicit integrationssparande
            const integrationUpdate = {
              externalIntegrations: integrationsCopy
            };
            
            const response = await axiosInstance.put(
              `/api/templates/${editingTemplate._id}`, 
              integrationUpdate
            );
            
            console.log("Integrationer sparade direkt:", response.data);
            
            // Hämta mallen igen för att verifiera att allt sparades korrekt
            const verifyResponse = await axiosInstance.get(`/api/templates/${editingTemplate._id}`);
            console.log("Verifiering efter direkt integrationssparande:", 
              verifyResponse.data.externalIntegrations);
            
            // Om svaret visar att integrationer inte sparades, visa varning
            if (!verifyResponse.data.externalIntegrations || 
                verifyResponse.data.externalIntegrations.length !== updatedIntegrations.length) {
              console.error("Varning: Integrationer kanske inte sparades korrekt!");
              alert("Varning: Sparandet av integrationsdata kan ha misslyckats. Försök igen.");
            }
          } catch (error) {
            console.error("Fel vid direkt sparning av integrationer:", error);
          }
        };
        
        // Kör den omedelbara sparningen
        saveIntegrationsImmediately();
      }
      
      // Återställ formulär
      setIsEditingIntegration(false);
      setSelectedExternalSystem('');
      setExternalApiKey('');
      setExternalClientId('');
      setExternalClientSecret('');
      setExternalUsername('');
      setExternalPassword('');
      setExternalDisplayName('');
      setExternalApiEndpoint('');
      setExternalRefreshInterval(1440);
      setEditingIntegrationIndex(-1);
      setDabasTestResult(null);
      
      // Bekräfta för användaren
      alert('Integration saved successfully!');
    } catch (error) {
      console.error("Error saving integration:", error);
      alert('Failed to save integration. Please try again.');
    }
  };
  
  const handleToggleIntegrationStatus = (index: number) => {
    setExternalIntegrations(prevIntegrations => 
      prevIntegrations.map((integration, i) => 
        i === index ? { ...integration, enabled: !integration.enabled } : integration
      )
    );
  };
  
  const handleSaveTemplate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate fields - ensure required properties
      if (!templateName.trim()) {
        setError('Template name is required');
        setLoading(false);
        return;
      }
      
      // Ensure all template fields have the required properties
      const validatedFields = templateFields.map(field => {
        return {
          ...field,
          id: field.id || `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          showDuringUpload: field.showDuringUpload !== false,
          showDuringEdit: field.showDuringEdit !== false,
          showInChainDropdown: field.type === 'separator' ? false : field.showInChainDropdown === true,
          showInWineDropdown: field.type === 'separator' ? false : field.showInWineDropdown === true,
          hideLabelInDisplay: field.hideLabelInDisplay === true,
          order: field.order || 0
        };
      });
      
      // Ensure external integrations data is properly prepared for saving
      // Create deep clones to avoid reference issues
      const cleanedIntegrations = externalIntegrations.map(integration => {
        // Explicitly include all fields for external integrations (Validoo, Dabas, etc.)
        return {
          system: integration.system,
          displayName: integration.displayName || undefined, // User-friendly name (e.g., "Dabas Production")
          fieldMapping: integration.fieldMapping ? {...integration.fieldMapping} : {},
          apiEndpoint: integration.apiEndpoint || undefined,
          apiKey: integration.apiKey || undefined,
          clientId: integration.clientId || undefined,
          clientSecret: integration.clientSecret || undefined,
          username: integration.username || undefined,
          password: integration.password || undefined,
          refreshInterval: integration.refreshInterval || 1440,
          lastSynced: integration.lastSynced || undefined,
          enabled: integration.enabled
        };
      });
      
      console.log('[SAVE TEMPLATE] Prepared external integrations:', JSON.stringify(cleanedIntegrations, null, 2));
      
      // Validera external integrations
      const hasValidooIntegration = cleanedIntegrations.some(
        i => i.system === 'validoo' && i.enabled && i.clientId && i.clientSecret
      );
      
      if (hasValidooIntegration) {
        console.log('[SAVE TEMPLATE] Valid Validoo integration found');
      }
      
      // Prepare request data - use simple object literals to avoid TypeScript issues
      const templateData = {
        name: templateName,
        description: templateDescription,
        categoryIds: selectedCategoryIds,
        fields: validatedFields,
        externalIntegrations: cleanedIntegrations,
        hideEmptyFieldsInDisplay,
        fullPageLayoutProfile,
      };
      
      console.log('[SAVE TEMPLATE] Saving template with data:', {
        name: templateData.name,
        description: templateData.description,
        categoryIds: templateData.categoryIds?.length,
        fieldsCount: templateData.fields?.length,
        externalIntegrationsCount: templateData.externalIntegrations?.length,
        hasValidooIntegration
      });
      
      let savedTemplate;
      
      if (editingTemplate) {
        // Update existing template
        const response = await axiosInstance.put(`/api/templates/${editingTemplate._id}`, templateData);
        savedTemplate = response.data;
        console.log('[SAVE TEMPLATE] Template updated successfully:', savedTemplate);
        
        if (onTemplateUpdated) {
          onTemplateUpdated();
        }
      } else {
        // Create new template
        const response = await axiosInstance.post('/api/templates', templateData);
        savedTemplate = response.data;
        console.log('[SAVE TEMPLATE] Template created successfully:', savedTemplate);
        
        if (onTemplateAdded) {
          onTemplateAdded();
        }
      }
      
      // Reset form
      setTemplateName('');
      setTemplateDescription('');
      setSelectedCategoryIds([]);
      setTemplateFields([]);
      setExternalIntegrations([]);
      setHideEmptyFieldsInDisplay(false);
      setFullPageLayoutProfile('other');
      setEditingTemplate(null);
      setTabValue(0);
      
      // Refresh template list
      fetchTemplates();
      
    } catch (error: any) {
      console.error('[SAVE TEMPLATE] Error saving template:', error);
      setError(error.response?.data?.message || 'Error saving template');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setSelectedCategoryIds([]);
    setTemplateFields([]);
    setHideEmptyFieldsInDisplay(false);
    setFullPageLayoutProfile('other');
    setTabValue(0); // Switch back to template list
  };
  
  // Make a direct schema update request to ensure template fields are saved correctly
  const updateTemplateSchema = async (templateId: string, fields: any[]) => {
    try {
      console.log(`Making direct schema update request for template ${templateId}`);
      console.log(`Sending ${fields.length} fields for schema update`);
      
      const response = await axiosInstance.put(`/api/templates/${templateId}/schema-update`, {
        fields: fields
      });
      
      console.log('Schema update response:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('Error updating template schema:', err);
      throw err;
    }
  };
  
  // Test Dabas connection
  const handleTestDabas = async () => {
    if (!dabasTestGtin.trim()) {
      alert('Ange ett GTIN att testa med');
      return;
    }
    
    setDabasTestLoading(true);
    setDabasTestResult(null);
    
    try {
      const response = await axiosInstance.post('/api/dabas/test', {
        gtin: dabasTestGtin.trim()
      });
      
      if (response.data.success) {
        setDabasTestResult({
          success: true,
          preview: response.data.preview,
          message: response.data.message
        });
      } else {
        setDabasTestResult({
          success: false,
          message: response.data.message || 'Test failed'
        });
      }
    } catch (error: any) {
      console.error('Dabas test error:', error);
      setDabasTestResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to test Dabas connection'
      });
    } finally {
      setDabasTestLoading(false);
    }
  };

  // Verifiera credentials för Validoo integration
  const handleVerifyCredentials = async () => {
    if (selectedExternalSystem === 'validoo') {
      if (!externalClientId.trim() || !externalClientSecret.trim() || !externalUsername.trim() || !externalPassword.trim()) {
        alert('All credentials (Client ID, Client Secret, Username and Password) are required for verification');
        return;
      }
      
      try {
        console.log("Attempting to verify Validoo credentials...");
        
        const response = await axiosInstance.post(`/api/external/validoo/verify-credentials`, {
          clientId: externalClientId,
          clientSecret: externalClientSecret,
          username: externalUsername,
          password: externalPassword
        });
        
        console.log("Verify response:", response);
        
        if (response.data.success) {
          alert(`Verification successful. ${response.data.message}`);
        } else {
          alert(`Verification failed: ${response.data.message}`);
        }
      } catch (error: any) {
        console.error("Verify credentials error:", error);
        
        // Mer detaljerad felhantering
        if (error.response) {
          // Servern svarade med en status-kod utanför 2xx range
          console.error("Error response data:", error.response.data);
          console.error("Error response status:", error.response.status);
          alert(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          // Begäran gjordes men inget svar mottogs
          console.error("No response received:", error.request);
          alert("No response from server. Is the server running?");
        } else {
          // Något gick fel vid uppställning av begäran
          console.error("Request error:", error.message);
          alert(`Error: ${error.message}`);
        }
      }
    } else if (selectedExternalSystem === 'erp') {
      // TODO: Add ERP credential verification
      alert('ERP credential verification not implemented yet');
    }
  };
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="template-management-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: '900px',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <Typography id="template-management-modal-title" variant="h5" component="h2" gutterBottom>
          Manage Upload Templates
        </Typography>
        
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Template List" />
          <Tab label={editingTemplate ? "Edit Template" : "Create Template"} />
          {editingTemplate && <Tab label="Fields" />}
          {editingTemplate && <Tab label="External Integrations" />}
        </Tabs>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TabPanel value={tabValue} index={0}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNewTemplate}
              >
                Add New Template
              </Button>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Alert severity="info">
                No templates found. Click "Add New Template" to create one.
              </Alert>
            ) : (
              <List>
                {templates.map((template) => (
                  <Paper key={template._id} sx={{ mb: 2 }}>
                    <ListItem
                      secondaryAction={
                        <Box>
                          <Tooltip title="Edit Template">
                            <IconButton 
                              edge="end" 
                              onClick={() => handleEditTemplate(template)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Set as Default">
                            <IconButton 
                              edge="end" 
                              onClick={() => handleSetDefaultTemplate(template._id)}
                              color={template.isDefault ? "primary" : "default"}
                            >
                              <StarIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton 
                              edge="end" 
                              onClick={(e) => handleMenuOpen(e, template._id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h6">
                              {template.name}
                            </Typography>
                            {template.isDefault && (
                              <Tooltip title="Default Template">
                                <StarIcon color="primary" sx={{ ml: 1, fontSize: '1rem' }} />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">{template.description}</Typography><br />
                            <Typography variant="caption" component="span">
                              Associated Categories: {template.categoryIds.length > 0
                                ? template.categoryIds
                                    .map(id => categories.find(c => c._id === id)?.name || 'Unknown')
                                    .join(', ')
                                : 'None'}
                            </Typography><br />
                            <Typography variant="caption" component="span" display="block">
                              Fields: {template.fields?.length || 0}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
            
            {/* Dropdown menu for template actions */}
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              MenuListProps={{
                dense: true,
                sx: { py: 1 }
              }}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  bgcolor: '#f8f8f8',
                  border: '1px solid rgba(0,0,0,0.08)'
                }
              }}
            >
              <MenuItem 
                onClick={() => {
                  if (selectedTemplateId) {
                    handleDeleteTemplate(selectedTemplateId);
                  }
                }}
                disabled={templates.find(t => t._id === selectedTemplateId)?.isDefault}
                sx={{ py: 1, px: 2, minHeight: 0, color: 'error.main' }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <DeleteIcon sx={{ fontSize: 17, color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 400 }}>Delete Template</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box>
            <TextField
              label="Template Name"
              fullWidth
              margin="normal"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
            
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              multiline
              rows={3}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={hideEmptyFieldsInDisplay}
                  onChange={(e) => setHideEmptyFieldsInDisplay(e.target.checked)}
                />
              }
              label="Dölj tomma fält i visningsläge"
              sx={{ mt: 1 }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="full-page-layout-profile-label">Profil för stora asset-sidan</InputLabel>
              <Select
                labelId="full-page-layout-profile-label"
                value={fullPageLayoutProfile}
                label="Profil för stora asset-sidan"
                onChange={(event) => setFullPageLayoutProfile(normalizeFullPageLayoutProfile(event.target.value))}
              >
                {FULL_PAGE_LAYOUT_PROFILE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Styr endast layouten i den stora asset-sidan. Panelen och uppladdningsformulär påverkas inte.
              </Typography>
            </FormControl>
            
            <FormControl component="fieldset" margin="normal" fullWidth>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Associated Categories
              </Typography>
              <Box sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', p: 1 }}>
                {categories.filter(category => !category.parentCategory).map(mainCategory => (
                  <Box key={mainCategory._id} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedCategoryIds.includes(mainCategory._id)}
                          onChange={handleCategoryChange}
                          value={mainCategory._id}
                        />
                      }
                      label={<Typography sx={{ fontWeight: 'bold' }}>{mainCategory.name}</Typography>}
                    />
                    
                    <Box sx={{ ml: 4 }}>
                      {categories
                        .filter(subCategory => {
                          if (typeof subCategory.parentCategory === 'string') {
                            return subCategory.parentCategory === mainCategory._id;
                          } else if (subCategory.parentCategory && typeof subCategory.parentCategory === 'object') {
                            return (subCategory.parentCategory as {_id: string})._id === mainCategory._id;
                          }
                          return false;
                        })
                        .map(subCategory => (
                          <FormControlLabel
                            key={subCategory._id}
                            control={
                              <Checkbox 
                                checked={selectedCategoryIds.includes(subCategory._id)}
                                onChange={handleCategoryChange}
                                value={subCategory._id}
                              />
                            }
                            label={subCategory.name}
                          />
                        ))
                      }
                    </Box>
                  </Box>
                ))}
              </Box>
            </FormControl>
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleSaveTemplate}>
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </Box>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          {editingTemplate && (
            <>
              <TemplateFieldEditor 
                fields={templateFields} 
                onChange={handleFieldsChange}
                onSave={handleSaveTemplate}
                loading={loading}
                isProductsTemplate={templateName.trim().toLowerCase() === 'produkter'}
                isWineTemplate={isWineTemplate}
                fullPageLayoutProfile={fullPageLayoutProfile}
              />
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={handleCancel} disabled={loading}>
                  Cancel Changes
                </Button>
                <Button variant="contained" color="primary" onClick={handleSaveTemplate} disabled={loading}>
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Update Template'}
                </Button>
              </Box>
            </>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          {editingTemplate && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">External Integrations</Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />} 
                  onClick={handleAddExternalIntegration}
                  disabled={isEditingIntegration}
                >
                  Add Integration
                </Button>
              </Box>
              
              {isEditingIntegration ? (
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {editingIntegrationIndex >= 0 ? 'Edit Integration' : 'Add Integration'}
                  </Typography>
                  
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="external-system-label">External System</InputLabel>
                    <Select
                      labelId="external-system-label"
                      value={selectedExternalSystem}
                      onChange={(e) => setSelectedExternalSystem(e.target.value)}
                      label="External System"
                    >
                      <MenuItem value="">Select a system</MenuItem>
                      {EXTERNAL_SYSTEMS.map(system => (
                        <MenuItem key={system.id} value={system.id}>
                          {system.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {selectedExternalSystem && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                      {EXTERNAL_SYSTEMS.find(s => s.id === selectedExternalSystem)?.description}
                    </Typography>
                  )}
                  
                  {/* Display Name field - for custom naming (especially useful for Custom API) */}
                  {selectedExternalSystem && (
                    <TextField
                      label="Display Name (optional)"
                      fullWidth
                      margin="normal"
                      value={externalDisplayName}
                      onChange={(e) => setExternalDisplayName(e.target.value)}
                      placeholder={`e.g., "${EXTERNAL_SYSTEMS.find(s => s.id === selectedExternalSystem)?.name} Production"`}
                      helperText="Custom name to display for this integration (optional)"
                    />
                  )}
                  
                  {selectedExternalSystem === 'validoo' && (
                    <>
                      <TextField
                        label="Client ID"
                        fullWidth
                        margin="normal"
                        value={externalClientId}
                        onChange={(e) => setExternalClientId(e.target.value)}
                        placeholder="Enter your Validoo client ID"
                        required
                        helperText="Your client ID for Validoo authentication (required)"
                      />
                      
                      <TextField
                        label="Client Secret"
                        fullWidth
                        margin="normal"
                        value={externalClientSecret}
                        onChange={(e) => setExternalClientSecret(e.target.value)}
                        placeholder="Enter your Validoo client secret"
                        required
                        type="password"
                        helperText="Your client secret for Validoo authentication (required)"
                      />
                      
                      <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        value={externalUsername}
                        onChange={(e) => setExternalUsername(e.target.value)}
                        placeholder="Enter your Validoo username"
                        required
                        helperText="Your username for Validoo authentication (required)"
                      />
                      
                      <TextField
                        label="Password"
                        fullWidth
                        margin="normal"
                        value={externalPassword}
                        onChange={(e) => setExternalPassword(e.target.value)}
                        placeholder="Enter your Validoo password"
                        required
                        type="password"
                        helperText="Your password for Validoo authentication (required)"
                      />
                      
                      <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={handleVerifyCredentials}
                        sx={{ mt: 1, mb: 2 }}
                      >
                        Verify Credentials
                      </Button>
                    </>
                  )}
                  
                  {selectedExternalSystem === 'dabas' && (
                    <>
                      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        Dabas använder en global API-nyckel som konfigureras på servern (miljövariabel DABAS_API_KEY).
                        Du behöver inte ange några autentiseringsuppgifter här.
                      </Alert>
                      
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Testa Dabas-anslutningen</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <TextField
                            label="Test GTIN"
                            size="small"
                            value={dabasTestGtin}
                            onChange={(e) => setDabasTestGtin(e.target.value)}
                            placeholder="07316150970340"
                            sx={{ flexGrow: 1 }}
                          />
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={handleTestDabas}
                            disabled={dabasTestLoading}
                          >
                            {dabasTestLoading ? <CircularProgress size={20} /> : 'Testa'}
                          </Button>
                        </Box>
                        
                        {dabasTestResult && (
                          <Box sx={{ mt: 2 }}>
                            {dabasTestResult.success ? (
                              <Alert severity="success">
                                <Typography variant="body2"><strong>Anslutning lyckades!</strong></Typography>
                                {dabasTestResult.preview && (
                                  <Box sx={{ mt: 1, fontSize: '0.85rem' }}>
                                    <div><strong>Produktnamn:</strong> {dabasTestResult.preview.produktnamn || '-'}</div>
                                    <div><strong>Artikelbenämning:</strong> {dabasTestResult.preview.artikelbenamning || '-'}</div>
                                    <div><strong>Varumärke:</strong> {dabasTestResult.preview.varumarke || '-'}</div>
                                    <div><strong>Tillverkare:</strong> {dabasTestResult.preview.tillverkare || '-'}</div>
                                    <div><strong>Storlek:</strong> {dabasTestResult.preview.storlek || '-'}</div>
                                  </Box>
                                )}
                              </Alert>
                            ) : (
                              <Alert severity="error">
                                {dabasTestResult.message}
                              </Alert>
                            )}
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                  
                  <TextField
                    label="API Endpoint"
                    fullWidth
                    margin="normal"
                    value={externalApiEndpoint}
                    onChange={(e) => setExternalApiEndpoint(e.target.value)}
                    placeholder="https://api.example.com/endpoint"
                    helperText="Leave empty to use the default endpoint"
                  />
                  
                  <TextField
                    label="Refresh Interval (minutes)"
                    fullWidth
                    type="number"
                    margin="normal"
                    value={externalRefreshInterval}
                    onChange={(e) => setExternalRefreshInterval(parseInt(e.target.value))}
                    helperText="How often to sync data (in minutes). Default: 1440 (daily)"
                    InputProps={{ inputProps: { min: 15, max: 10080 } }} // 15 min to 7 days
                  />
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => setIsEditingIntegration(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleSaveExternalIntegration}
                    >
                      Save Integration
                    </Button>
                  </Box>
                </Paper>
              ) : (
                <>
                  {externalIntegrations.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No external integrations defined. Click "Add Integration" to create one.
                    </Alert>
                  ) : (
                    <List>
                      {externalIntegrations.map((integration, index) => (
                        <Paper key={index} sx={{ mb: 2 }}>
                          <ListItem
                            secondaryAction={
                              <Box>
                                <Tooltip title={integration.enabled ? "Disable" : "Enable"}>
                                  <Switch
                                    checked={integration.enabled}
                                    onChange={() => handleToggleIntegrationStatus(index)}
                                    color="primary"
                                  />
                                </Tooltip>
                                <Tooltip title="Edit Integration">
                                  <IconButton 
                                    edge="end" 
                                    onClick={() => handleEditExternalIntegration(index)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Integration">
                                  <IconButton 
                                    edge="end" 
                                    onClick={() => handleDeleteExternalIntegration(index)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                          >
                            <ListItemIcon>
                              <CloudSyncIcon color={integration.enabled ? "primary" : "disabled"} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="subtitle1">
                                    {(integration as any).displayName || EXTERNAL_SYSTEMS.find(s => s.id === integration.system)?.name || integration.system}
                                  </Typography>
                                  {(integration as any).displayName && (
                                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                      ({EXTERNAL_SYSTEMS.find(s => s.id === integration.system)?.name || integration.system})
                                    </Typography>
                                  )}
                                  <Chip 
                                    label={integration.enabled ? 'Active' : 'Disabled'} 
                                    size="small" 
                                    color={integration.enabled ? 'success' : 'default'} 
                                    sx={{ ml: 1 }} 
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {integration.apiEndpoint && (
                                    <Typography variant="caption" display="block">
                                      Endpoint: {integration.apiEndpoint}
                                    </Typography>
                                  )}
                                  {integration.apiKey && (
                                    <Typography variant="caption" display="block">
                                      API Key: {integration.apiKey.substring(0, 4)}••••••••••••
                                    </Typography>
                                  )}
                                  {integration.clientId && (
                                    <Typography variant="caption" display="block">
                                      Client ID: {integration.clientId.substring(0, 4)}••••••••••••
                                    </Typography>
                                  )}
                                  {integration.clientSecret && (
                                    <Typography variant="caption" display="block">
                                      Client Secret: ••••••••••••••••••••••
                                    </Typography>
                                  )}
                                  {integration.username && (
                                    <Typography variant="caption" display="block">
                                      Username: {integration.username}
                                    </Typography>
                                  )}
                                  {integration.password && (
                                    <Typography variant="caption" display="block">
                                      Password: ••••••••••••
                                    </Typography>
                                  )}
                                  <Typography variant="caption" display="block">
                                    Refresh: {integration.refreshInterval 
                                      ? `Every ${integration.refreshInterval} minutes` 
                                      : 'Daily'
                                    }
                                    {integration.lastSynced && ` (Last synced: ${new Date(integration.lastSynced).toLocaleString()})`}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Field Mappings: {Object.keys(integration.fieldMapping || {}).length}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {(integration.system === 'validoo' || integration.system === 'dabas') && (
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {integration.system === 'validoo' ? 'Validoo' : 'Dabas'} Field Mappings
                              </Typography>
                              <Typography variant="body2">
                                Configure which {integration.system === 'validoo' ? 'Validoo' : 'Dabas'} fields map to your template fields in the Fields tab by marking 
                                them as external fields and selecting {integration.system === 'validoo' ? 'Validoo' : 'Dabas'} as the data source.
                              </Typography>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                startIcon={<EditIcon />} 
                                onClick={() => {
                                  setTabValue(2); // Switch to Fields tab
                                }}
                                sx={{ mt: 1 }}
                              >
                                Configure Field Mappings
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </List>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                    External integrations allow automatic population of template fields from systems like Validoo, ERP, and other sources.
                    Define the connections here and then map them to specific fields in the Fields tab.
                  </Typography>
                </>
              )}
            </Box>
          )}
        </TabPanel>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} color="secondary">
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default TemplateManagementModal; 